import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { TradePlanData } from '../../../../../../classes/tradePlan/tradePlanData';
import { useTradePlanFormStore } from '../../../../../../classes/tradePlan/tradePlanProvider';
import { colors } from '../../../../../../colorVars';
import { addSymbol } from '../../../../../../stores/websocketStore/utils/outgoingMessages/symbols/addSymbol';
import { removeSymbol } from '../../../../../../stores/websocketStore/utils/outgoingMessages/symbols/removeSymbol';
import { addSavedTradePlanConfig } from '../../../../../../stores/websocketStore/utils/outgoingMessages/tradePlan/savetradeplanconfig/addSavedTradePlanConfig';
import { updateSavedTradePlanConfig } from '../../../../../../stores/websocketStore/utils/outgoingMessages/tradePlan/savetradeplanconfig/updateSavedTradePlanConfig';
import { TEMPLATE_TYPES, addTemplateTypeToPayload } from '../../../../../forms/tradeplans/templateFunctions/templateTypeUtils';
import {
  buildSymbolSubscription,
  planSymbolSubscriptionMessages,
  sameSymbolSubscription,
} from '../../../../../../stores/websocketStore/utils/symbolSubscriptionLifecycle';
import { useWebsocketStore } from '../../../../../../stores/websocketStore/websocketProvider';
import { BidAskBox } from '../../../../../atomic/atomicComponentLibrary/bidAskBox/BidAskBox';
import { TextButton } from '../../../../../atomic/atomicComponentLibrary/buttons/textButton/TextButton';
import { Dropdown } from '../../../../../atomic/atomicComponentLibrary/dropdown/Dropdown';
import { BoxSwitch } from '../../../../../atomic/atomicComponentLibrary/switches/boxSwitch/BoxSwitch';
import { SymbolInput } from '../../../../../atomic/atomicComponentLibrary/symbolnput/SymbolInput';
import { Body4, fontSizes } from '../../../../../atomic/text/Text';
import {
  mapAccountsFromBalances,
  getBrokerageForSelectedAccount,
  isClosedSelectedAccount,
  resolveMarketDataBrokerage,
} from '../../../../../../utils/accountRouting';
import { defaultTemplateConstant } from '../../TradingModule';
import AllowTemplatesWarningModal from './AllowTemplatesWarningModal';
import style from './TopRow.module.css';
import { TopRowInputLabel } from './TopRowInputLabel';
import { resetSymbolScopedChartState } from '../../utils/resetSymbolScopedChartState';
// add bid ask component

type Props = {
  templatesList: string[];
  selectedTemplate: string;
  setSelectedTemplate: (value: string) => void;
  allowTemplates: boolean;
  setAllowTemplates: (value: boolean) => void;
  timeUnit: any;
  setTimeUnit: (value: any) => void;
  candleDuration: any;
  setCandleDuration: (value: any) => void;
  numberOfCandles: any;
  setNumberOfCandles: (value: any) => void;
  showCandleNumbers: boolean;
  setShowCandleNumbers: (value: boolean) => void;
};

export const TopRow = observer(
  ({
    templatesList,
    selectedTemplate,
    setSelectedTemplate,
    allowTemplates,
    setAllowTemplates,
    timeUnit,
    setTimeUnit,
    candleDuration,
    setCandleDuration,
    numberOfCandles,
    setNumberOfCandles,
    showCandleNumbers,
    setShowCandleNumbers,
  }: Props) => {
    const [openModal, setOpenModal] = useState(false);
    const activeSubscriptionRef = React.useRef(null);

    const tradePlanForm: TradePlanData = useTradePlanFormStore();
    const { savedTradePlanTemplatesStore, accountBalanceStore, connected } =
      useWebsocketStore();
    const listOfAccountObjects = mapAccountsFromBalances(accountBalanceStore?.balances).map(
      (account) => ({
        accountNumber: account.accountNumber,
        nickName: account.nickname,
        accountType: account.accountType,
        brokerage: account.brokerage,
        isClosed: account.isClosed,
      })
    );
    const firstOpenLiveAccount =
      listOfAccountObjects.find(
        (account) => account.accountNumber !== 'BACKTEST' && !account.isClosed
      ) ?? null;
    const marketDataBrokerage = resolveMarketDataBrokerage({
      accountNumber: tradePlanForm.accountNumber,
      accounts: listOfAccountObjects,
      currentBrokerage: tradePlanForm.brokerage,
      dataSource: tradePlanForm.dataSource,
      symbol: tradePlanForm.symbol,
    });

    const handleSymbolChange = (value: string) => {
      const nextSymbol = String(value ?? '')
        .trim()
        .toUpperCase();
      const currentSymbol = String(tradePlanForm.symbol ?? '')
        .trim()
        .toUpperCase();

      tradePlanForm.setSymbol(nextSymbol);

      if (tradePlanForm.instrument === 'option' && nextSymbol.startsWith('/')) {
        tradePlanForm.setInstrument('future_option');
      } else if (tradePlanForm.instrument === 'future_option' && !nextSymbol.startsWith('/')) {
        tradePlanForm.setInstrument('option');
      }

      if (nextSymbol !== currentSymbol) {
        resetSymbolScopedChartState(tradePlanForm);
      }
    };

    const saveToDefaultTemplate = () => {
      const tradePlanObject = tradePlanForm.returnJson();
      if (templatesList.includes(defaultTemplateConstant)) {
        const key =
          savedTradePlanTemplatesStore.savedTemplates[defaultTemplateConstant]
            .key;
        updateSavedTradePlanConfig({
          tradePlanObject: addTemplateTypeToPayload(
            tradePlanObject,
            TEMPLATE_TYPES.STANDARD
          ),
          key,
          configName: defaultTemplateConstant,
          templateType: TEMPLATE_TYPES.STANDARD,
        });
      } else {
        addSavedTradePlanConfig(
          defaultTemplateConstant,
          addTemplateTypeToPayload(tradePlanObject, TEMPLATE_TYPES.STANDARD),
          TEMPLATE_TYPES.STANDARD
        );
      }
      tradePlanForm.setValidationStatus({
        status: true,
        message: 'Default configuration updated.',
      });
    };

    useEffect(() => {
      const nextSubscription = buildSymbolSubscription({
        symbol: tradePlanForm.symbol,
        dataSource: tradePlanForm.dataSource,
        brokerage: marketDataBrokerage,
        watchlist_name: '_tradingModule',
      });

      if (!connected) {
        activeSubscriptionRef.current = nextSubscription;
        return;
      }

      const messages = sameSymbolSubscription(
        activeSubscriptionRef.current,
        nextSubscription
      )
        ? nextSubscription
          ? [{ messageType: 'addSymbol', payload: nextSubscription }]
          : []
        : planSymbolSubscriptionMessages(
            activeSubscriptionRef.current,
            nextSubscription
          );

      messages.forEach(({ messageType, payload }) => {
        if (messageType === 'addSymbol') {
          addSymbol(payload);
        } else {
          removeSymbol(payload);
        }
      });

      activeSubscriptionRef.current = nextSubscription;
    }, [connected, tradePlanForm.symbol, tradePlanForm.dataSource, marketDataBrokerage]);

    useEffect(() => {
      return () => {
        if (activeSubscriptionRef.current) {
          removeSymbol(activeSubscriptionRef.current);
          activeSubscriptionRef.current = null;
        }
      };
    }, []);

    useEffect(() => {
      const selectedBrokerage = getBrokerageForSelectedAccount(
        tradePlanForm.accountNumber,
        listOfAccountObjects
      );
      if (selectedBrokerage) {
        if (selectedBrokerage !== tradePlanForm.brokerage) {
          tradePlanForm.setBrokerage(selectedBrokerage);
        }
        return;
      }

      if (
        tradePlanForm.accountNumber === 'BACKTEST' &&
        tradePlanForm.dataSource !== 'LIVE' &&
        tradePlanForm.brokerage !== 'BACKTEST'
      ) {
        tradePlanForm.setBrokerage('BACKTEST');
      }
    }, [
      listOfAccountObjects,
      tradePlanForm.accountNumber,
      tradePlanForm.brokerage,
      tradePlanForm.dataSource,
    ]);

    useEffect(() => {
      if (tradePlanForm.dataSource !== 'LIVE') {
        return;
      }

      if (!isClosedSelectedAccount(tradePlanForm.accountNumber, listOfAccountObjects)) {
        return;
      }

      if (firstOpenLiveAccount) {
        tradePlanForm.setAccountNumber(firstOpenLiveAccount.accountNumber);
        if (firstOpenLiveAccount.brokerage) {
          tradePlanForm.setBrokerage(firstOpenLiveAccount.brokerage);
        }
        tradePlanForm.setValidationStatus({
          status: false,
          message: `Selected account ${tradePlanForm.accountNumber} is closed. Switched to ${firstOpenLiveAccount.accountNumber}.`,
        });
        return;
      }

      tradePlanForm.setValidationStatus({
        status: false,
        message: `Selected account ${tradePlanForm.accountNumber} is closed. No open live account is available.`,
      });
    }, [
      firstOpenLiveAccount,
      listOfAccountObjects,
      tradePlanForm,
      tradePlanForm.accountNumber,
      tradePlanForm.dataSource,
    ]);

    return (
      <div className={style.topRow}>
        <TopRowInputLabel label="Duration" margin="0 5px 0 0">
          <Dropdown
            color="white"
            backgroundColor="grey"
            value={candleDuration}
            onChange={(value) => setCandleDuration(value)}
            fontSize={fontSizes.body4}
          >
            <option value={1}>1</option>
            <option value={2}>2</option>
            <option value={3}>3</option>
            <option value={4}>4</option>
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={15}>15</option>
            <option value={30}>30</option>
            <option value={60}>60</option>
            <option value={120}>120</option>
          </Dropdown>
        </TopRowInputLabel>
        <TopRowInputLabel label="Time Unit" margin="0 5px 0 0">
          <Dropdown
            color="white"
            backgroundColor="grey"
            value={timeUnit}
            onChange={(value) => setTimeUnit(value)}
            fontSize={fontSizes.body4}
          >
            <option value="MINUTES">Minutes</option>
            <option value="DAYS">Days</option>
            <option value="WEEKS">Weeks</option>
            <option value="MONTHS">Months</option>
            <option value="QUARTERS">Quarters</option>
            <option value="YEARS">Years</option>
          </Dropdown>
        </TopRowInputLabel>
        <TopRowInputLabel label="# of Candles" margin="0 5px 0 0">
          <Dropdown
            color="white"
            backgroundColor="grey"
            value={numberOfCandles}
            onChange={(value) => setNumberOfCandles(value)}
            fontSize={fontSizes.body4}
          >
            <option value={5}>5 Candles</option>
            <option value={10}>10 Candles</option>
            <option value={15}>15 Candles</option>
            <option value={20}>20 Candles</option>
            <option value={30}>30 Candles</option>
            <option value={40}>40 Candles</option>
            <option value={50}>50 Candles</option>
          </Dropdown>
        </TopRowInputLabel>
        <TopRowInputLabel label="Candle #" margin="0 5px 0 0">
        <BoxSwitch
          fontSize={fontSizes.body4}
          setValue={(value) => setShowCandleNumbers(value)}
          labels={["#'s", 'Off']}
          values={[true, false]}
          title="Display candle numbers."
          selectedColor="green"
          notSelectedColor="grey"
          color="white"
          controlledValue={showCandleNumbers}
        />
        </TopRowInputLabel>
        <TopRowInputLabel label="Symbol" margin="0 5px 0 0">
        <SymbolInput
          stateSetterFunction={handleSymbolChange}
          initialValue={tradePlanForm.symbol}
          brokerage={marketDataBrokerage}
          fontSize={fontSizes.body4}
          color="white"
          backgroundColor="grey"
          width="50px"
        />
        </TopRowInputLabel>
        <TopRowInputLabel label="Bid/Ask" margin="0 5px 0 0">
        <BidAskBox
          symbol={tradePlanForm.symbol}
          dataSource={tradePlanForm.dataSource}
          brokerage={marketDataBrokerage}
        />
        </TopRowInputLabel>
        <TopRowInputLabel label="Data Source" margin="0 5px 0 0">
        <Dropdown
          color="white"
          backgroundColor="grey"
          value={tradePlanForm.dataSource}
          onChange={(value) => tradePlanForm.setDataSource(value)}
          fontSize={fontSizes.body4}
        >
          <option value="SIMULATED">Sim</option>
          <option value="LIVE">Live</option>
        </Dropdown>
        </TopRowInputLabel>
        <TopRowInputLabel label="Account" margin="0 5px 0 0">
        <Dropdown
          color="white"
          backgroundColor="grey"
          fontSize={fontSizes.body4}
          onChange={(value) => {
            if (value === 'BACKTEST') {
              tradePlanForm.setAccountNumber('BACKTEST');
              if (tradePlanForm.dataSource !== 'LIVE') {
                tradePlanForm.setBrokerage('BACKTEST');
              }
              return;
            }

            const selected = listOfAccountObjects.find(
              (obj) => obj.accountNumber === value
            );
            tradePlanForm.setAccountNumber(value);
            if (selected?.brokerage) {
              tradePlanForm.setBrokerage(selected.brokerage);
            }
          }}
          value={tradePlanForm.accountNumber || 'BACKTEST'}
        >
          <option value="BACKTEST">Backtest</option>
          {listOfAccountObjects.map((obj) => {
            if (obj.accountNumber !== 'BACKTEST') {
              return (
                <option
                  key={obj.accountNumber}
                  value={obj.accountNumber}
                  disabled={obj.isClosed}
                >
                  {buildAccountLabel(obj)}
                </option>
              );
            } else return null;
          })}
        </Dropdown>
        </TopRowInputLabel>
        <div className={style.labelAndCheckbox}>
          <input
            disabled={true}
            className={style.checkbox}
            type="checkbox"
            checked={allowTemplates}
            onChange={({ target }) => {
              if (target.checked) {
                setOpenModal(true);
              } else {
                setAllowTemplates(false);
              }
            }}
          />
          <Body4 color={colors.lightGrey}>Enable Templates</Body4>
        </div>
        <TopRowInputLabel label="Load Template" margin="0 5px 0 0">
        <Dropdown
          disabled={!allowTemplates}
          color="white"
          backgroundColor="grey"
          fontSize={fontSizes.body4}
          onChange={(value) => setSelectedTemplate(value)}
          value={selectedTemplate}
        >
          <option value="none">None</option>
          {templatesList.map((template) => {
            let string = template;
            if (string.length > 16) string = template.slice(0, 16) + '...';
            return (
              <option key={template} value={template}>
                {string}
              </option>
            );
          })}
        </Dropdown>
        </TopRowInputLabel>
        <TextButton
          loading={false}
          clickHandler={saveToDefaultTemplate}
          margin="13px 5px 0 0"
          backgroundColor="blue"
          fontColor="white"
          size="small"
        >
          Save Default
        </TextButton>
       
        <AllowTemplatesWarningModal
          open={openModal}
          handleClose={() => setOpenModal(false)}
          setAllowTemplates={setAllowTemplates}
        />
      </div>
    );
  }
);

const buildAccountLabel = (account: {
  nickName?: string;
  accountNumber?: string;
  accountType?: string;
  brokerage?: string;
  isClosed?: boolean;
}) => {
  const parts: string[] = [];
  if (account.nickName) parts.push(account.nickName);
  if (account.accountNumber) parts.push(account.accountNumber);
  if (account.accountType) parts.push(account.accountType);
  if (account.brokerage && account.brokerage !== 'none') parts.push(account.brokerage);
  if (account.isClosed) parts.push('Closed');
  return parts.length > 0 ? parts.join(' - ') : 'Account';
};
